import {
  CompanyMetricForm,
  MetricDataForm,
  MetricForm,
  MetricGroupForm,
  MetricGroupFormWrapper,
  SelectableMetricGroupForm,
  SelectableMetricGroupFormWrapper,
} from '../../dto/MetricGroupForm';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { getIsMetricDataProvided, MetricGroup } from '../../dto/MetricGroup';
import { Metric } from '../../dto/Metric';
import { MetricData, MetricDataRole, MetricPeriodType } from '../../dto/MetricData';
import { LocalDate } from '../../../../core/pojo/date-variable';
import { MetricCalculationService } from './metric-calculation.service';
import { CompanyMetric } from '../../dto/CompanyMetric';

export class MetricFillService {
  static initForm(groups: MetricGroup[]): MetricGroupFormWrapper {
    return new FormGroup({
      groups: new FormArray<MetricGroupForm>(groups.map((group) => this.createGroupForm(group))),
    });
  }

  /**
   * Initializes the FormGroup with parsed controls from param.
   * Afterward, sorts the groups by their 'selected' status. Updates it's index.
   *
   * @param groups with the metrics and metric data.
   */
  static initSelectableForm(groups: MetricGroup[]): SelectableMetricGroupFormWrapper {
    const groupsForm = new FormGroup({
      groups: new FormArray<SelectableMetricGroupForm>(
        groups.map((group) =>
          this.initSelectableGroupForm(group)
        )
      ),
    });
    this.sortGroups(groupsForm);
    return groupsForm;
  }

  /**
   * Sort groups by 'selected' status.
   * Updates group's index.
   *
   * Example:
   *
   * | Name    | Priority | Is Selected |
   * |---------|----------|-------------|
   * | Group 1 |    1     |    true     |
   * | Group 2 |    2     |    false    |
   * | Group 3 |    3     |    true     |
   *
   * After sorting:
   *
   * | Name    | Priority | Is Selected |
   * |---------|----------|-------------|
   * | Group 1 |    1     |     true    |
   * | Group 3 |    2     |     true    |
   * | Group 2 |    3     |     false   |
   *
   * @param groups with the metrics and metric data.
   */
  static sortGroups(groups: SelectableMetricGroupFormWrapper): void {
    groups.controls.groups.controls
      .sort((g1, g2) =>
        (g2.value.isSelected ? 1 : 0) - (g1.value.isSelected ? 1 : 0)
      );
    groups.controls.groups.controls.forEach((group, index) => group.controls.priority.patchValue(index));
  }

  static initSelectableGroupForm(group: MetricGroup): SelectableMetricGroupForm {
    const formGroup = this.createGroupForm(group) as SelectableMetricGroupForm;
    formGroup.addControl(
      'isSelected',
      new FormControl<boolean>(group.metrics.some((metric) => !metric.companyMetrics[0].isHidden)));
    formGroup.addControl(
      'isExpanded',
      new FormControl<boolean>(false));
    formGroup.addControl(
      'isMetricDataProvided',
      new FormControl<boolean>(getIsMetricDataProvided(group)));
    formGroup.addControl(
      'visibleMetricsAmount',
      new FormControl<number>(group.metrics.filter((metric) => !metric.companyMetrics[0].isHidden).length));
    return formGroup;
  }

  static createGroupForm(group: MetricGroup): MetricGroupForm {
    return new FormGroup({
      uuid: new FormControl(group.uuid),
      name: new FormControl(group.name),
      priority: new FormControl(group.priority),
      organizationUUID: new FormControl(group.organizationUUID),
      role: new FormControl(group.role),
      metrics: new FormArray<MetricForm>(group.metrics.map((metric) => this.createMetricForm(metric))),
      temp: new FormGroup({
        isSomeMetricsSelected: new FormControl<boolean>(group.temp.isSomeMetricsSelected),
        isAllMetricsSelected: new FormControl<boolean>(group.temp.isAllMetricsSelected),
      }),
    });
  }

  static createMetricForm(metric: Metric): MetricForm {
    return new FormGroup({
      uuid: new FormControl(metric.uuid),
      name: new FormControl(metric.name),
      priority: new FormControl(metric.priority),
      props: new FormControl(metric.props),
      dataProps: new FormControl(metric.dataProps),
      companyMetrics: new FormArray<CompanyMetricForm>(metric.companyMetrics.map(
        (companyMetric) => this.createCompanyMetricForm(companyMetric))),
      additionalClass: new FormControl(''),
      isSelected: new FormControl(true),
    });
  }

  static createCompanyMetricForm(companyMetric: CompanyMetric): CompanyMetricForm {
    return new FormGroup({
      uuid: new FormControl(companyMetric.uuid),
      priority: new FormControl(companyMetric.priority),
      isHidden: new FormControl(companyMetric.isHidden),
      companyUUID: new FormControl(companyMetric.companyUUID),
      metricData: new FormArray<MetricDataForm>(companyMetric.metricData.map((metricData) => this.createMetricDataForm(metricData))),
    });
  }

  static createMetricDataForm(metricData: MetricData): MetricDataForm {
    return new FormGroup({
      uuid: new FormControl(metricData.uuid),
      rowValue: new FormControl(metricData.rowValue),
      period: new FormControl(metricData.period),
      role: new FormControl(metricData.role),
      periodType: new FormControl(metricData.periodType),
    });
  }

  static createMetricData(metric: Metric, group: MetricGroup, date: LocalDate, role: MetricDataRole): void {
    const metricData: MetricData = this.initializeMetricData(date, role);
    if (metric.isStarting()) {
      metricData.rowValue = MetricCalculationService.getPreviousEnding(group, date, role === MetricDataRole.ACTUAL);
    }
    metric.companyMetrics[0].metricData.push(metricData);
  }

  static initializeMetricData(date: LocalDate, role: MetricDataRole): MetricData {
    const metricData: MetricData = new MetricData();
    metricData.period = date;
    metricData.rowValue = null;
    metricData.role = role;
    metricData.periodType = MetricPeriodType.MONTHLY;

    return metricData;
  }
}
